import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { tagHotjarRecording } from '../../helpers/util'
import { Redirect } from 'react-router'
import useRequest from '../../hooks/useRequest'
import AppRoutes from '../../routes/appRoutes'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import CardInfo from '../../components/CardInfo/CardInfo'
import CellLocation from '../../components/Table/components/CellLocation'
import CellLink from '../../components/Table/components/CellLink'
import PurchaseOrder from './components/PurchaseOrder'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import Warning from '../../svg/Warning'
import CarouselImages from '../../components/CarouselImages/CarouselImages'
import TimeLineSteps from '../../components/TimeLineSteps/TimeLineSteps'
import CustomDivider from '../../components/CardInfo/components/CustomDivider'
import InfoLine from '../../components/CardInfo/components/InfoLine'
import useScreen from '../../hooks/useScreen'
import useAlert from '../../hooks/useAlert'
import useAuth from '../../hooks/useAuth'
import { useHistory } from 'react-router-dom'
import { getPendingRequestById } from '../../../../indexedDb/pendingRequests'
import { useNetworkStatus } from '../../utils/networkStatus'
import { formatTimestampToDate } from '../../utils/formatTimestampToDate'
import { getRequestDetailsById } from '../../../../indexedDb/requestsDetails'
import MessageTimeSinceLastUpdate from '../../components/MessageTimeSinceLastUpdate/MessageTimeSinceLastUpdate'
import ErrorCard from '../CreateRequestStepper.js/components/ErrorCard'
import { parse } from 'qs'
import { isTimeStamp } from '../../utils/isTimeStamp'
import PendingMessage from '../../components/PendingMessage.js/PendingMessage'
import InformationCard from '../CreateRequestStepper.js/components/InformationCard'
import NeedsReviewWarningCard from '../CreateRequestStepper.js/components/NeedsReviewWarningCard'

const ViewServiceRequestPage = props => {
    const { match } = props
    const { isOffline } = useNetworkStatus()
    const { user } = useAuth()
    const { handleChangeAlert } = useAlert()
    const { changeLoading } = useGlobalLoading()
    const { isMobile, isTablet } = useScreen()
    const history = useHistory()

    const [request, setRequest] = useState([])
    const [isPOEstiable, setIsPOEstiable] = useState(false)
    const [errorMessage, setErrorMessage] = useState(false)
    const [downloadServiceError, setDownloadServiceError] = useState(false)
    const [images, setImages] = useState([])
    const [l1ntoWO, setL1ntoWO] = useState('')
    const [title, setTitle] = useState('')
    const [isPendingRequest, setIsPendingRequest] = useState(false)
    const {
        handleGetRequestInfo,
        handleUpdatePO,
        changeNeedsUpdate,
        changeNeedsUpdatePM,
    } = useRequest()
    const equipmentId = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.equipmentId

    const onGetRequestInfoSuccess = response => {
        let requestInfo = response?.data?.request
        if (requestInfo?.workOrderId) {
            setL1ntoWO(requestInfo.workOrderId)
        }
        setRequest(requestInfo)
        setTitle(
            isMobile
                ? requestInfo?.label
                : `Request Details: ${requestInfo?.label}`,
        )
        setIsPOEstiable(requestInfo?.isPOEditable)
        setImages(response?.data?.images)
        changeLoading(false)
    }

    const onGetRequestInfoError = () => {
        tagHotjarRecording('API Error')
        // let message = data?.response?.data?.message
        changeLoading(false)
    }

    function showErrorAlert(
        message,
        title = 'An error has occurred',
        type = 'error',
    ) {
        handleChangeAlert({
            title,
            message,
            type,
        })
    }

    useEffect(() => {
        const fetchPendingRequestInfo = async () => {
            let requestId = match?.params?.requestId
            changeLoading(true)
            let pendingRequestFromIndexDB

            const numericRequestId = Number(requestId)
            if (isTimeStamp(numericRequestId)) {
                pendingRequestFromIndexDB = await getPendingRequestById(
                    numericRequestId,
                )
            }

            if (pendingRequestFromIndexDB) {
                setIsPendingRequest(true)
                pendingRequestFromIndexDB.status === 'Pending'
                    ? setTitle('Pending Request')
                    : setTitle('Needs Review')

                setRequest(pendingRequestFromIndexDB)
                setIsPOEstiable(pendingRequestFromIndexDB?.isPOEditable)
            } else if (isOffline) {
                try {
                    const offlineRequest = await getRequestDetailsById(
                        requestId,
                    )
                    if (offlineRequest) {
                        setRequest(offlineRequest)
                    } else {
                        console.warn('Request not found in IndexedDB')
                    }
                } catch (error) {
                    console.error(
                        'Error fetching request from IndexedDB:',
                        error,
                    )
                }
            } else {
                try {
                    await handleGetRequestInfo(
                        requestId,
                        'service',
                        onGetRequestInfoSuccess,
                        onGetRequestInfoError,
                    )
                } catch (error) {
                    console.error('Error fetching request:', error)
                }
            }

            changeLoading(false)
        }

        fetchPendingRequestInfo()
    }, [isOffline, match?.params?.requestId])

    const onUpdatePOError = data => {
        let message = data?.response?.data?.message
        changeLoading(false)
        showErrorAlert(message)
    }

    const onUpdatePOSuccess = response => {
        let poNumber = response?.data?.poNumber
        request.poNumber = poNumber
        setRequest(request)
        setIsPOEstiable(false)
        changeLoading(false)
        changeNeedsUpdate({ dashboard: true, request: true })
        changeNeedsUpdatePM({ plannedMaintenance: true })
        handleChangeAlert({
            title: 'Purchase Order Updated',
            message: 'Your PO number has been updated.',
            type: 'success',
        })
    }

    const submitPurchaseOrder = poInfo => {
        changeLoading(true)
        poInfo.requestId = request?.salesforceId
        poInfo.userName = user?.first_name + ' ' + user?.last_name
        handleUpdatePO(poInfo, onUpdatePOSuccess, onUpdatePOError)
    }

    const activeTab = isOffline
        ? 'overviewTab'
        : request?.typeDisplay === 'PM'
        ? 'plannedMaintenanceTab'
        : 'serviceHistoryTab'

    const renderHeader = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start p-4 bg-white border-b border-grayLines lg:gap-4 gap-2">
                <p
                    role="button"
                    onClick={() => {
                        equipmentId
                            ? history.push(
                                  `${AppRoutes.createRepairRequestFlow()}?equipmentId=${equipmentId}`,
                              )
                            : history?.goBack()
                    }}
                    className="text-textDarkGray text-sm font-medium"
                >
                    {'< Go Back'}
                </p>
                <p className="text-black90 font-semibold lg:text-lg text-2xl">
                    {title}
                </p>
                <TimeLineSteps
                    request={request}
                    title=""
                    steps={request?.statusTimeline}
                    isAbsolute={isTablet || isMobile}
                    setErrorMessage={setErrorMessage}
                    setDownloadServiceError={setDownloadServiceError}
                />
                {/* Service Status */}
            </div>
        )
    }

    const renderEstimateInformationContent = () => {
        if (request?.showEstimateDetails) {
            const quote = request?.quote
            return (
                <div className="w-full h-auto flex flex-col items-start justify-start lg:py-2 py-0">
                    <InfoLine
                        label="Estimate Number"
                        value={
                            quote?.name ? (
                                <CellLink
                                    key="linkToWO"
                                    text={quote?.name}
                                    to={AppRoutes.viewEstimateRequest(
                                        quote?.id,
                                    )}
                                />
                            ) : (
                                '-'
                            )
                        }
                    />
                    <CustomDivider />
                    <InfoLine
                        label="Status"
                        value={
                            quote?.status ? (
                                <StatusBadge
                                    displayClass={quote?.status}
                                    extraBadgeClass="-ml-3"
                                />
                            ) : (
                                '-'
                            )
                        }
                    />
                    <CustomDivider />
                    <InfoLine
                        label="Valid Until"
                        value={quote?.prettyValidUntil || '-'}
                        prompText="This estimate will be canceled after this expiration date"
                        isLast
                    />
                </div>
            )
        }

        return (
            <div className="w-full h-auto flex flex-col items-start justify-start lg:py-2 py-0 ">
                <p className="text-textDarkerGray text-smMinus leading-4">
                    Your estimate is being processed. Once it is created, you
                    will see the details here.
                </p>
            </div>
        )
    }

    const renderRequestInformationContent = () => {
        const requestLabel = !isPendingRequest
            ? request?.label || '-'
            : 'Unavailable'
        const typeOfRequest = !isPendingRequest
            ? request?.typeDisplay || '-'
            : request?.showType
        const requestDate = !isPendingRequest
            ? request?.formattedDate || '-'
            : formatTimestampToDate(request?.id)
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start lg:py-2 py-0">
                <InfoLine label="Request Number" value={requestLabel} />
                <CustomDivider />
                <InfoLine label="Type of Request" value={typeOfRequest} />
                <CustomDivider />
                <InfoLine
                    label="Submitted by"
                    value={request?.requestedByName || '-'}
                />
                <CustomDivider />
                <InfoLine label="Submitted on" value={requestDate} isLast />
            </div>
        )
    }

    const renderAssetDetailsContent = () => {
        const requestAssetName =
            request?.customerAlias || request?.equipmentName
        const requestId = request?.equipmentSFId || request?.equipmentId
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start py-2">
                <InfoLine
                    label="Customer Unit #"
                    value={
                        (
                            <CellLink
                                key="linkToAsset"
                                text={requestAssetName}
                                to={
                                    requestId
                                        ? AppRoutes.fleetAsset(requestId) +
                                          '?activeTab=' +
                                          activeTab
                                        : '/'
                                }
                                page="fleetAssetPage"
                                activeTab={activeTab}
                                // disableLink={request?.assetMoved || isOffline}
                                // isOffline={isOffline}
                                // needRelative={true}
                            />
                        ) || '-'
                    }
                />
                <CustomDivider />
                <InfoLine
                    label="Location"
                    value={
                        request?.address || request?.locationName ? (
                            <CellLocation
                                key="assetLocation"
                                address={request?.address || '-'}
                                label={request?.locationName || '-'}
                                isInCard
                            />
                        ) : (
                            '-'
                        )
                    }
                />
            </div>
        )
    }

    const renderDescriptionIssue = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start py-2">
                <InfoLine
                    label="Asset is down?"
                    value={
                        <div className="flex flex-row items-center justify-start">
                            {request?.assetIsDown && (
                                <Warning className="mr-1" />
                            )}
                            <p>
                                {request?.assetIsDown
                                    ? 'Yes, asset is down'
                                    : 'No'}
                            </p>
                        </div>
                    }
                />
                <CustomDivider />
                <InfoLine
                    label="What needs to be done?"
                    value={request?.description || '-'}
                />
                <CustomDivider />
                <InfoLine
                    label="Location on site"
                    value={request?.locationDetails || '-'}
                />
                <CustomDivider />
                <InfoLine
                    label="Other details"
                    value={request?.additionalDetails || '-'}
                />
                <CustomDivider />
                <InfoLine
                    label="Fault code display"
                    value={request?.faultCode || '-'}
                />
            </div>
        )
    }

    const renderPurchaseOrderNumber = () => {
        return (
            <PurchaseOrder
                poNumber={request?.poNumber || null}
                handleSubmit={submitPurchaseOrder}
                isEditable={isPOEstiable}
            />
        )
    }

    const renderImages = images => {
        let allImages = []
        if (isPendingRequest) {
            allImages = images.map(img =>
                img.data instanceof Blob
                    ? URL.createObjectURL(img.data)
                    : img.data,
            )
        } else {
            allImages = images?.files?.map(a => a?.url)
        }

        return (
            <div className="w-full lg:h-100 h-auto relative">
                <CarouselImages
                    heightImage={isMobile ? 'auto' : 400}
                    images={allImages}
                />
            </div>
        )
    }

    const renderAttachments = () => {
        const hasImages = isPendingRequest
            ? request?.images && request?.images.length > 0
            : images?.files?.length > 0

        const imageList = isPendingRequest ? request?.images : images
        return (
            <div className="w-full h-auto rounded-2xl overflow-hidden max-w-full max-h-full my-2">
                {hasImages ? (
                    renderImages(imageList)
                ) : (
                    <p className="text-sm text-center text-textDarkerGray font-medium">
                        No files found
                    </p>
                )}
            </div>
        )
    }

    const renderContentInfo = () => {
        return (
            <div className="lg:w-220 w-full m-auto h-auto flex flex-col items-start justify-start gap-6 lg:mt-12 mt-0 lg:p-0 p-4 lg:mb-0 mb-24">
                {request.status === 'Pending' && (
                    <InformationCard request={request} />
                )}
                {request.status === 'Needs Review' && (
                    <NeedsReviewWarningCard request={request} />
                )}

                {request?.showEstimateBox && (
                    <CardInfo
                        title="Service Estimate Details"
                        subTitle="Find the details of the estimate below."
                        content={renderEstimateInformationContent()}
                        isOverflowVisible
                    />
                )}
                <CardInfo
                    title="Request Information"
                    content={renderRequestInformationContent()}
                />

                <CardInfo
                    title="Asset Details"
                    content={renderAssetDetailsContent()}
                />

                {renderPurchaseOrderNumber()}

                <CardInfo
                    title="Description of Issue"
                    content={renderDescriptionIssue()}
                />

                <CardInfo
                    title="Attachments"
                    hasPaddingContent={!isMobile}
                    content={renderAttachments()}
                />

                {isOffline && isPendingRequest && <PendingMessage bottom={0} />}

                {isOffline &&
                    !isPendingRequest &&
                    !errorMessage &&
                    !downloadServiceError && (
                        <div
                            className={`fixed right-0 p-0 z-50 ${
                                isMobile || isTablet
                                    ? 'bottom-24 w-full'
                                    : 'bottom-0 w-96'
                            }`}
                        >
                            <MessageTimeSinceLastUpdate message="You’re viewing the latest information available from" />
                        </div>
                    )}
                {isOffline && !isPendingRequest && errorMessage && (
                    <div
                        className={`fixed right-0 p-0 z-50 ${
                            isMobile || isTablet
                                ? 'bottom-24 w-full'
                                : 'bottom-0 w-96'
                        }`}
                    >
                        <ErrorCard
                            error="Status history unavailable offline."
                            closeButton
                        />
                    </div>
                )}
                {isOffline && !isPendingRequest && downloadServiceError && (
                    <div
                        className={`fixed right-0 p-0 z-50 ${
                            isMobile || isTablet
                                ? 'bottom-24 w-full'
                                : 'bottom-0 w-96'
                        }`}
                    >
                        <ErrorCard
                            error="Unable to download report offline."
                            closeButton
                        />
                    </div>
                )}
            </div>
        )
    }

    return (
        <ContentLayout isRelative>
            {l1ntoWO ? (
                <Redirect to={AppRoutes.viewServiceRequest(l1ntoWO)} />
            ) : null}
            {renderHeader()}
            {renderContentInfo()}
        </ContentLayout>
    )
}

ViewServiceRequestPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            requestId: PropTypes.string,
        }),
    }),
    location: PropTypes.shape({
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
}

export default ViewServiceRequestPage
